import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BufferLoading, EmptyState, QueueItems } from '~/shared-components';
import { Button } from '@bufferapp/ui';
import ComposerPopover from '~/composer-popover';
import getErrorBoundary from '~/web/components/ErrorBoundary';
import ProfilesDisconnectedBanner from '~/profiles-disconnected-banner';
import { PublishingNotification } from '../PublishingNotification';

const ErrorBoundary = getErrorBoundary(true);

const containerStyle = {
  paddingBottom: '1rem',
};

const loadingContainerStyle = {
  width: '100%',
  height: '100%',
  textAlign: 'center',
  paddingTop: '5rem',
};

const topBarContainerStyle = {
  display: 'flex',
};

const composerStyle = {
  flexGrow: '1',
};

const loadMoreButtonStyle = {
  textAlign: 'center',
  paddingBottom: '3rem',
};

const NoPostsPublished = ({ total, has30DaySentPostsLimitFeature }) => {
  if (typeof total === 'undefined' || total > 0) {
    return null;
  }
  return (
    <>
      <EmptyState
        height="initial"
        title={
          has30DaySentPostsLimitFeature
            ? 'You haven’t published any posts with this account in the past 30 days!'
            : 'You haven’t published any posts with this account!'
        }
        heroImg="https://s3.amazonaws.com/buffer-publish/images/empty-sent2x.png"
        heroImgSize={{ width: '270px', height: '150px' }}
      />
    </>
  );
};

NoPostsPublished.propTypes = {
  total: PropTypes.number,
  has30DaySentPostsLimitFeature: PropTypes.bool,
};

NoPostsPublished.defaultProps = {
  total: undefined,
  has30DaySentPostsLimitFeature: true,
};

const PendingTiktok = ({
  total,
  loading,
  items,
  onEditClick,
  onShareAgainClick,
  onCampaignTagClick,
  onComposerCreateSuccess,
  showComposer,
  editMode,
  isManager,
  isDisconnectedProfile,
  has30DaySentPostsLimitFeature,
  hasFirstCommentFlip,
  hasCampaignsFeature,
  moreToLoad,
  tabId,
  profileId,
  page,
  loadMore,
  analyzeCrossSale,
  fetchPendingTiktokPosts,
  profileServiceType,
  profileService,
  hasAnalyticsOnPosts,
  hasTwitterImpressions,
  onConfirmDismissComposer,
}) => {
  useEffect(() => {
    fetchPendingTiktokPosts();
  }, [profileId]);

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <BufferLoading size={64} />
      </div>
    );
  }

  const loadMorePosts = () => {
    loadMore({ profileId, page, tabId });
  };

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      <NoPostsPublished
        has30DaySentPostsLimitFeature={has30DaySentPostsLimitFeature}
        total={total}
      />

      {total > 0 ? (
        <>
          <PublishingNotification />
          <div style={containerStyle}>
            <div style={topBarContainerStyle}>
              {showComposer && (
                <div style={composerStyle}>
                  <ComposerPopover
                    onSave={onComposerCreateSuccess}
                    type="pendingTiktok"
                    onConfirmDismissComposer={onConfirmDismissComposer}
                  />
                </div>
              )}
            </div>
            {showComposer && editMode && (
              <ComposerPopover
                onSave={onComposerCreateSuccess}
                type="sent"
                onConfirmDismissComposer={onConfirmDismissComposer}
              />
            )}
            <QueueItems
              items={items}
              onEditClick={onEditClick}
              onShareAgainClick={onShareAgainClick}
              onCampaignTagClick={onCampaignTagClick}
              isManager={isManager}
              hasFirstCommentFlip={hasFirstCommentFlip}
              hasCampaignsFeature={hasCampaignsFeature}
              analyzeCrossSale={analyzeCrossSale}
              profileServiceType={profileServiceType}
              profileService={profileService}
              hasAnalyticsOnPosts={hasAnalyticsOnPosts}
              hasTwitterImpressions={hasTwitterImpressions}
              shouldHideCreatePostButton
            />
          </div>
          {moreToLoad && (
            <div style={loadMoreButtonStyle}>
              <Button
                type="primary"
                label="Load More"
                onClick={loadMorePosts}
              />
            </div>
          )}
        </>
      ) : null}
    </ErrorBoundary>
  );
};

PendingTiktok.propTypes = {
  fetchPendingTiktokPosts: PropTypes.func.isRequired,
  onConfirmDismissComposer: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  moreToLoad: PropTypes.bool, // eslint-disable-line
  page: PropTypes.number, // eslint-disable-line
  has30DaySentPostsLimitFeature: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      date: PropTypes.string,
      queueItemType: PropTypes.string,
      dayOfWeek: PropTypes.string,
      hasCommentEnabled: PropTypes.bool,
    })
  ).isRequired,
  total: PropTypes.number,
  showComposer: PropTypes.bool,
  analyzeCrossSale: PropTypes.bool,
  editMode: PropTypes.bool,
  onComposerCreateSuccess: PropTypes.func.isRequired,
  onEditClick: PropTypes.func,
  onShareAgainClick: PropTypes.func,
  onCampaignTagClick: PropTypes.func,
  isManager: PropTypes.bool,
  isDisconnectedProfile: PropTypes.bool,
  hasFirstCommentFlip: PropTypes.bool,
  hasCampaignsFeature: PropTypes.bool,
  hasAnalyticsOnPosts: PropTypes.bool,
  hasTwitterImpressions: PropTypes.bool,
};

PendingTiktok.defaultProps = {
  loading: true,
  moreToLoad: false,
  page: 1,
  total: 0,
  showComposer: false,
  analyzeCrossSale: false,
  editMode: false,
  isManager: true,
  hasFirstCommentFlip: false,
  hasCampaignsFeature: false,
  isDisconnectedProfile: false,
  onEditClick: () => {},
  onShareAgainClick: () => {},
  onCampaignTagClick: () => {},
  hasAnalyticsOnPosts: false,
  hasTwitterImpressions: false,
  has30DaySentPostsLimitFeature: false,
};

export default PendingTiktok;
